/*
--------------------------------------------------------
Skin Rocks - GTM Custom Events
--------------------------------------------------------

Implements the Skin Rocks data layer specification for:

- Page impressions
- Product impressions
- Product interactions
- Cart interactions
- Newsletter sign-ups
- Quiz interactions
- Promotional click tracking

Checkout, shipping, payment and purchase events
are implemented separately via Shopify Customer Events.

--------------------------------------------------------
*/

window.dataLayer = window.dataLayer || [];

gtmPageImpression();

document.addEventListener("DOMContentLoaded", () => {
  gtmProductView();
  gtmProductListViews();
  gtmProductClicks();
  gtmPlpAdClick();
});

/**
 * Pushes the page_impression event.
 * Fired once on page load to capture page metadata.
 */
function gtmPageImpression() {
  window.dataLayer.push({
    event: "page_impression",
    page_info: {
      url: window.location.href,
      virtual_url: window.location.origin + window.location.pathname,
      page_path: window.location.pathname,
      page_title: document.title,
      page_type: getPageType(),
      page_referrer: document.referrer || undefined,
      market: window.skinRocksData?.market,
      currency: window.skinRocksData?.currency,
      trigger_type: "hard"
    }
  });
}

/**
 * Pushes product_detail_impression on PDPs.
 * Uses the product JSON embedded in the product form.
 */
function gtmProductView() {
  const container = document.querySelector("product-info .product-form");
  if (!container) return;

  const items = getProductJson(container);
  if (!items.length) return;

  pushEcommerceEvent("product_detail_impression", {
    value: getItemsValue(items),
    items
  });
}

/**
 * Tracks product list impressions.
 * Fires after a product list remains visible for 3 seconds.
 * Prevents duplicate impressions for the same list.
 */
function gtmProductListViews() {
  const productLists = [...document.querySelectorAll("[js-gtm-product-list]")];

  productLists.forEach((list) => {
    let timer;
    let fired = false;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (fired) return;

        if (entry.isIntersecting) {
          timer = setTimeout(() => {
            const items = getProductJson(list);

            if (!items.length) return;

            fired = true;

            pushEcommerceEvent("product_list_impression", {
              value: getItemsValue(items),
              items
            });

            observer.unobserve(list);
          }, 3000);
        } else {
          clearTimeout(timer);
        }
      });
    });

    observer.observe(list);
  });
}

/**
 * Tracks clicks on product cards.
 * Sends select_item before navigating to the PDP.
 */
window.gtmProductClicks = function gtmProductClicks(container){
  let products = [...document.querySelectorAll(".product-card")];

  if (container) {
    products = [...container.querySelectorAll(".product-card")];
  }

  products.forEach((product) => {
    const link = product.querySelector("a.card-url");
    if (!link) return;

    link.addEventListener("click", (event) => {
      event.preventDefault();

      const items = getProductJson(product);

      if (!items.length) {
        window.location.href = link.href;
        return;
      }

      pushEcommerceEvent("select_item", {
        value: getItemsValue(items),
        items
      });

      setTimeout(() => {
        window.location.href = link.href;
      }, 150);
    });
  });
};

/**
 * Tracks products being added to cart.
 * Supports PDP add-to-cart and collection quick add.
 */
window.gtmAddToCart = function gtmAddToCart(container) {
  let product = container.closest('.product-card');

  if (!product) {
    product = container.closest('.product-form');
  }

  if (!product) return;

  const items = getProductJson(product);
  if (!items.length) return;

  const totalQuantity = items.reduce(
    (total, item) => total + Number(item.quantity || 1),
    0
  );

  const addedProducts =
    totalQuantity > 1 || items.length > 1
      ? 'multiple'
      : 'single';

  const normalizedItems = items.map(item => ({
    ...item,
    added_products: addedProducts
  }));

  const isQuickAdd = Boolean(
    container.closest('.product-card') ||
    container.closest('quick-add-modal')
  );

  pushEcommerceEvent('add_to_cart', {
    quickadd: isQuickAdd,
    added_products: addedProducts,
    value: getItemsValue(normalizedItems),
    items: normalizedItems
  });
};

/**
 * Tracks products added to cart from non-standard flows.
 * Used when product data is passed directly instead of read from DOM JSON.
 */
window.gtmAddToCartItems = function gtmAddToCartItems(items, options = {}) {
  const addedProducts =
    options.added_products ||
    (items.length > 1 ? 'multiple' : 'single');

  const normalizedItems = items
    .map((item, index) => normalizeItem({
      ...item,
      index,
      item_variant_id: item.item_variant_id || item.variant_id || item.id,
      quantity: item.quantity || 1,
      added_products: addedProducts
    }))
    .filter(Boolean);

  if (!normalizedItems.length) return;

  pushEcommerceEvent('add_to_cart', {
    quickadd: Boolean(options.quickadd),
    added_products: addedProducts,
    value: getItemsValue(normalizedItems),
    items: normalizedItems
  });
};

/**
 * Tracks products being removed from cart.
 * Retained for GA4 compatibility.
 */
window.gtmRemoveFromCart = function gtmRemoveFromCart(container) {
  const product = container.closest(".cart-item");
  if (!product) return;

  const items = getProductJson(product);
  if (!items.length) return;

  pushEcommerceEvent("remove_from_cart", {
    value: getItemsValue(items),
    items
  });
};

/**
 * Tracks products removed from cart from non-standard flows.
 * Used when product data is passed directly instead of read from DOM JSON.
 */
window.gtmRemoveFromCartItems = function(items) {
  const normalizedItems = items
    .map((item, index) =>
      normalizeItem({
        ...item,
        index,
        item_variant_id: item.item_variant_id || item.variant_id || item.id,
        quantity: item.quantity || 1
      })
    )
    .filter(Boolean);

  if (!normalizedItems.length) return;

  pushEcommerceEvent("remove_from_cart", {
    value: getItemsValue(normalizedItems),
    items: normalizedItems
  });
};

/**
 * Tracks cart views.
 * Supports manual and automatic cart openings.
 */
window.gtmViewCart = function gtmViewCart(container, cartOpening = "manual") {
  if (!container) return;

  const items = getProductJson(container);
  if (!items.length) return;

  pushEcommerceEvent("view_cart", {
    value: getItemsValue(items),
    cart_opening: toSnakeCase(cartOpening),
    items
  });
};

/**
 * Tracks successful newsletter subscriptions.
 * Clears user_data immediately afterwards.
 */
window.gtmNewsletterSignup = function gtmNewsletterSignup(email, sha256HashedEmail) {
  window.dataLayer.push({
    event: "newsletter_signup",
    user_data: {
      logged_in: false,
      email,
      sha256_hashed_email: sha256HashedEmail
    }
  });

  window.dataLayer.push({ user_data: null });
};

/**
 * Tracks the first interaction with the quiz.
 * Fires once per page session.
 */
window.gtmQuizStart = function gtmQuizStart() {
  if (window.__gtmQuizStarted) return;
  window.__gtmQuizStarted = true;

  window.dataLayer.push({
    event: "quiz_start"
  });
};

/**
 * Tracks successful quiz submissions.
 * Clears user_data immediately afterwards.
 */
window.gtmQuizSubmit = function gtmQuizSubmit(userData = {}) {
  window.dataLayer.push({
    event: "quiz_submit",
    user_data: userData
  });

  window.dataLayer.push({ user_data: null });
};

/**
 * Tracks promotional collection card clicks.
 */
function gtmPlpAdClick() {
  const items = [...document.querySelectorAll("[js-gtm-plp-ad]")];

  items.forEach((item) => {
    const links = [...item.querySelectorAll("a")];

    links.forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();

        window.dataLayer.push({
          event: "plp_ad_click",
          collection: toSnakeCase(item.getAttribute("js-gtm-plp-ad")),
          content_title: toSnakeCase(item.querySelector(".card__heading")?.textContent)
        });

        setTimeout(() => {
          window.location.href = link.href;
        }, 150);
      });
    });
  });
}

/**
 * Returns product data from embedded JSON scripts.
 * Normalizes values into the Skin Rocks data layer format.
 */
function getProductJson(data) {
  const items = [...data.querySelectorAll("[js-gtm-product-json]")];

  return items
    .map((item) => {
      try {
        return normalizeItem(JSON.parse(item.innerHTML));
      } catch {
        return null;
      }
    })
    .filter(Boolean);
}

/**
 * Clears the e-commerce object and pushes a new e-commerce event.
 */
function pushEcommerceEvent(eventName, ecommerce) {
  window.dataLayer.push({ ecommerce: null });

  window.dataLayer.push({
    event: eventName,
    ecommerce
  });
}

/**
 * Calculates e-commerce value from item price × quantity.
 */
function getItemsValue(items) {
  return Number(
    items.reduce((total, item) => {
      const price = Number(item.price || 0);
      const quantity = Number(item.quantity || 1);
      return total + price * quantity;
    }, 0).toFixed(2)
  );
}
  
/**
 * Normalizes product data into the Skin Rocks data layer schema.
 */
function normalizeItem(item) {
  return {
    item_id: String(item.item_id || item.product_id || ''),
    item_variant_id: String(item.item_variant_id || item.variant_id || ''),
    item_name: toSnakeCase(item.item_name || item.title || item.product_title),
    index: Number(item.index || 0),
    item_brand: toSnakeCase(item.item_brand || item.vendor || 'Skin Rocks'),
    item_category: toSnakeCase(item.item_category || item.product_type),
    item_category2: toSnakeCase(item.item_category2),
    item_category3: toSnakeCase(item.item_category3),
    item_variant: toSnakeCase(
      item.item_variant ||
      item.variant_title ||
      item.sku
    ),
    ...(item.added_products
      ? { added_products: item.added_products }
      : {}),
    discount: Number(item.discount || 0),
    price: Number(item.price || 0),
    quantity: Number(item.quantity || 1)
  };
}

/**
 * Determines the type of the current page.
 */
function getPageType() {
  const template = window.skinRocksData?.template;
  const path = window.location.pathname;

  if (path.includes("the-routine-quiz")) return "quiz";
  if (path.includes("find-a-facialist")) return "find_facialist";
  if (path.startsWith("/account/orders")) return "order_history";
  if (path.startsWith("/account/addresses")) return "addresses";
  if (template?.includes('register')) return 'register';
  if (template?.includes('reset_password')) return 'reset_password';

  switch (template) {
    case "index": return "start";
    case "product": return "product_detail_page";
    case "collection": return "product_listing_page";
    case "search": return "search";
    case "cart": return "cart";
    case "login": return "login";
    case "account": return "account";
    case "blog": return "blog";
    case "article": return "article";
    case "page": return "content_page";
    case "404": return "404";
    default: return "other";
  }
}

/**
 * Converts a string to snake case.
 */
function toSnakeCase(value) {
  if (value === undefined || value === null || value === "") return undefined;

  return String(value)
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
}